/**
 * ============================================================================
 * RFLXNS Theme — Product Page Runtime
 * ============================================================================
 * Canonical PDP client behavior for the Bluorng-style product rebuild.
 *
 * Owns:
 * - option and variant resolution
 * - quantity controls
 * - size guide drawer
 * - mobile carousel dot state
 * - tab switching
 * - add-to-bag and buy-now submission through the shared cart service
 * ============================================================================
 */
document.addEventListener('DOMContentLoaded', () => {
  const root = document.querySelector('[data-pdp-root]');
  if (!root) return;

  // --------------------------------------------------------------------------
  // Shared DOM lookups
  // --------------------------------------------------------------------------
  const productForm = document.getElementById('product-form');
  const variantInput = productForm?.querySelector('#variant-id-input');
  const variantsScript = root.querySelector('[data-product-variants]');
  const priceOutput = root.querySelector('[data-pdp-price]');
  const feedbackEl = root.querySelector('[data-pdp-feedback]');
  const optionButtons = Array.from(root.querySelectorAll('[data-pdp-option-button]'));
  const quantityInput = root.querySelector('[data-pdp-quantity-input]');
  const quantityButtons = Array.from(root.querySelectorAll('[data-pdp-qty]'));

  const sizeGuideDrawer = document.querySelector('[data-size-guide-drawer]');
  const sizeGuideOpenButtons = Array.from(root.querySelectorAll('[data-size-guide-open]'));
  const sizeGuideCloseButtons = document.querySelectorAll('[data-size-guide-close]');

  const tabButtons = Array.from(root.querySelectorAll('[data-pdp-tab-trigger]'));
  const tabPanels = Array.from(root.querySelectorAll('[data-pdp-tab-panel]'));

  const mobileCarousel = root.querySelector('[data-pdp-mobile-carousel]');
  const mobileSlides = Array.from(root.querySelectorAll('[data-pdp-mobile-slide]'));
  const mobileDots = Array.from(root.querySelectorAll('[data-pdp-mobile-dot]'));

  let variants = [];
  try {
    variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
  } catch (e) {
    variants = [];
  }
  const inventoryScript = root.querySelector('[data-product-variants-inventory]');
  let variantsInventory = [];
  try {
    variantsInventory = inventoryScript ? JSON.parse(inventoryScript.textContent) : [];
  } catch (e) {
    variantsInventory = [];
  }

  // --------------------------------------------------------------------------
  // Formatting helpers
  // --------------------------------------------------------------------------
  /**
   * Format price into the store money pattern (INR). Handles raw paise integers
   * (e.g. 149900), whole numbers, and decimal strings (e.g. "1499.00").
   */
  function formatMoney(cents) {
    if (cents == null || cents === '') return '';
    let value;
    const num = Number(cents);
    if (typeof cents === 'string' && cents.includes('.')) {
      value = Math.round(parseFloat(cents));
    } else if (num >= 10000 && Number.isInteger(num)) {
      value = Math.round(num / 100);
    } else {
      value = Math.round(num);
    }
    return `₹${value.toLocaleString('en-IN')}`;
  }

  /**
   * Write a short inline status message below the PDP CTA row.
   */
  function setFeedback(message, type = '') {
    if (!feedbackEl) return;
    feedbackEl.textContent = message || '';
    feedbackEl.classList.remove('is-error', 'is-success');
    if (type) feedbackEl.classList.add(type);
  }

  const optionGroups = Array.from(root.querySelectorAll('[data-pdp-option-group]'));

  // --------------------------------------------------------------------------
  // Variant and option state
  // --------------------------------------------------------------------------
  /**
   * Check if the user has selected a value for every required option group.
   */
  function hasSelectedAllOptions() {
    if (!optionGroups.length) return true;
    return optionGroups.every((group) => {
      return group.querySelector('[data-pdp-option-button].is-active') !== null;
    });
  }

  /**
   * Read the currently selected option values in option-position order.
   */
  function getSelectedOptions() {
    const selected = [];
    optionButtons.forEach((button) => {
      if (button.classList.contains('is-active')) {
        const position = Number(button.dataset.optionPosition);
        selected[position - 1] = button.dataset.optionValue;
      }
    });
    return selected;
  }

  /**
   * Find the Shopify variant that matches the currently selected option values.
   */
  function resolveVariant() {
    if (!variants.length) return null;
    if (!hasSelectedAllOptions()) return null;
    const selected = getSelectedOptions();

    return (
      variants.find((variant) =>
        variant.options.every((optionValue, index) => {
          return selected[index] === undefined || optionValue === selected[index];
        })
      ) || null
    );
  }

  /**
   * Enable or disable the PDP CTAs based on variant availability and waitlist state.
   */
  function syncCtaAvailability(variant) {
    const isProductSoldOut = root.hasAttribute('data-is-product-sold-out');
    const ctaRow = productForm?.querySelector('.rflxns-pdp__cta-row') || root.querySelector('#rflxns-pdp-cta-row');
    const addBtn = productForm?.querySelector('[data-product-action="add"]');
    const buyBtn = productForm?.querySelector('[data-product-action="buy-now"]');
    const waitlistBox = root.querySelector('[data-pdp-waitlist-box]');

    if (isProductSoldOut) {
      if (ctaRow) ctaRow.style.display = 'none';
      if (waitlistBox) {
        waitlistBox.classList.add('is-visible');
      }
      setFeedback('');
      return;
    }

    if (!hasSelectedAllOptions()) {
      if (ctaRow) ctaRow.style.display = '';
      if (waitlistBox) waitlistBox.classList.remove('is-visible');
      if (addBtn) {
        addBtn.textContent = 'Add to bag';
        addBtn.disabled = false;
        addBtn.removeAttribute('aria-disabled');
      }
      if (buyBtn) {
        buyBtn.style.display = '';
        buyBtn.disabled = false;
        buyBtn.removeAttribute('aria-disabled');
      }
      return;
    }

    const isAvailable = Boolean(variant?.available);
    if (!isAvailable) {
      const selectedSize = variant?.title || '';
      // Completely hide the Add to Bag and Buy Now system as requested!
      if (ctaRow) ctaRow.style.display = 'none';
      if (waitlistBox) {
        waitlistBox.classList.add('is-visible');
        const titleEl = waitlistBox.querySelector('.rflxns-pdp__waitlist-title');
        const subEl = waitlistBox.querySelector('.rflxns-pdp__waitlist-sub');
        const bodyInput = waitlistBox.querySelector('#rflxns-waitlist-body');
        const prodTitle = document.querySelector('.rflxns-pdp__title')?.textContent?.trim() || '';

        if (titleEl) {
          titleEl.textContent = selectedSize ? `REMIND ME WHEN IN STOCK · SIZE ${selectedSize}` : 'REMIND ME WHEN IN STOCK';
        }
        if (subEl) {
          subEl.textContent = selectedSize ? `Leave your email. We will notify you the moment Size ${selectedSize} is back in stock.` : 'Leave your email. We will send you an invite the moment this piece is back in production.';
        }
        if (bodyInput && selectedSize) {
          bodyInput.value = `Restock notification request for ${prodTitle} (Size: ${selectedSize})`;
        }
      }
      setFeedback('');
    } else {
      if (ctaRow) ctaRow.style.display = '';
      if (addBtn) {
        addBtn.textContent = 'Add to bag';
        addBtn.disabled = false;
        addBtn.removeAttribute('aria-disabled');
      }
      if (buyBtn) {
        buyBtn.style.display = '';
        buyBtn.disabled = false;
        buyBtn.removeAttribute('aria-disabled');
      }
      if (waitlistBox) {
        waitlistBox.classList.remove('is-visible');
      }
      setFeedback('');
    }
  }

  /**
   * Push the resolved variant ID and price back into the visible PDP state.
   */
  function syncVariantState() {
    const variant = resolveVariant();
    if (!variant) {
      if (variantInput && !root.hasAttribute('data-has-only-default')) {
        variantInput.value = '';
      }
      syncCtaAvailability(null);
      updateLowStockWarning(null);
      updateStickyBarState(null);
      return null;
    }

    if (variantInput) {
      variantInput.value = variant.id;
    }

    if (priceOutput) {
      priceOutput.textContent = formatMoney(variant.price);
    }

    const comparePriceOutput = root.querySelector('[data-pdp-compare-price]');
    const discountBadge = root.querySelector('[data-pdp-discount-badge]');
    if (comparePriceOutput) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePriceOutput.textContent = formatMoney(variant.compare_at_price);
        comparePriceOutput.style.display = '';
        if (discountBadge) {
          const discount = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          discountBadge.textContent = `${discount}% OFF`;
          discountBadge.style.display = '';
        }
      } else {
        const anchorPrice = variant.price >= 200000 ? Math.round((variant.price * 1.4) / 100) : 2499;
        comparePriceOutput.textContent = `₹${anchorPrice.toLocaleString('en-IN')}`;
        comparePriceOutput.style.display = '';
        if (discountBadge) {
          const currentInr = Math.round(variant.price / 100);
          const discount = Math.round(((anchorPrice - currentInr) / anchorPrice) * 100);
          if (discount > 0) {
            discountBadge.textContent = `${discount}% OFF`;
            discountBadge.style.display = '';
          } else {
            discountBadge.textContent = 'LIMITED DROP';
            discountBadge.style.display = '';
          }
        }
      }
    }

    syncCtaAvailability(variant);
    updateLowStockWarning(variant);
    updateStickyBarState(variant);
    updateOptionPillAvailability();
    return variant;
  }

  /**
   * Update the warning when variant has low stock in inventory.
   */
  function updateLowStockWarning(variant) {
    const warningEl = root.querySelector('[data-pdp-low-stock-warning]');
    if (!warningEl) return;

    if (!variant) {
      warningEl.textContent = '';
      warningEl.style.display = 'none';
      return;
    }

    const inv = variantsInventory.find(v => v.id === variant.id);
    if (inv && inv.available) {
      const qty = inv.inventory_quantity;
      if (qty > 0 && qty <= 5) {
        warningEl.innerHTML = `<span class="rflxns-pdp__low-stock-dot" aria-hidden="true"></span> ONLY ${qty} PIECE${qty > 1 ? 'S' : ''} REMAINING IN SIZE ${(variant.title || '').toUpperCase()}`;
        warningEl.style.display = 'inline-flex';
      } else {
        warningEl.textContent = '';
        warningEl.style.display = 'none';
      }
    } else {
      warningEl.textContent = '';
      warningEl.style.display = 'none';
    }
  }

  /**
   * Update sticky bottom bar values.
   */
  function updateStickyBarState(variant) {
    const stickyPrice = document.querySelector('[data-pdp-sticky-price]');
    const stickyVariantLabel = document.querySelector('[data-pdp-sticky-variant-label]');
    if (stickyPrice && variant) {
      stickyPrice.textContent = formatMoney(variant.price);
    }
    if (stickyVariantLabel) {
      stickyVariantLabel.textContent = variant ? `[ ${variant.title} ]` : `[ SELECT SIZE ]`;
    }
  }

  /**
   * Crosspatch option pills so users cannot select unavailable combinations.
   */
  function updateOptionPillAvailability() {
    if (!variants.length) return;

    const groups = Array.from(root.querySelectorAll('[data-pdp-option-group]'));
    groups.forEach((group) => {
      const currentPosition = Number(group.dataset.optionPosition);
      const pills = Array.from(group.querySelectorAll('[data-pdp-option-button]'));

      pills.forEach((pill) => {
        const pillValue = pill.dataset.optionValue;

        const isCombinableAvailable = variants.some((variant) => {
          if (!variant.available) return false;

          return variant.options.every((optionValue, idx) => {
            const pos = idx + 1;
            if (pos === currentPosition) {
              return optionValue === pillValue;
            }
            const activePill = root.querySelector(`[data-pdp-option-button][data-option-position="${pos}"].is-active`);
            if (!activePill) return true;
            return optionValue === activePill.dataset.optionValue;
          });
        });

        pill.classList.toggle('is-sold-out', !isCombinableAvailable);
        pill.setAttribute('aria-disabled', isCombinableAvailable ? 'false' : 'true');
      });
    });
  }

  /**
   * Toggle the active pill styling for option buttons and recalculate the variant.
   */
  function selectOption(button) {
    const position = button.dataset.optionPosition;
    if (!position) return;

    optionButtons
      .filter((candidate) => candidate.dataset.optionPosition === position)
      .forEach((candidate) => {
        candidate.classList.remove('is-active');
        candidate.setAttribute('aria-pressed', 'false');
      });

    button.classList.add('is-active');
    button.setAttribute('aria-pressed', 'true');

    const valueDisplay = root.querySelector(`[data-pdp-selected-option-value="${position}"]`);
    if (valueDisplay) {
      valueDisplay.textContent = (button.dataset.optionValue || '').toUpperCase();
    }

    const groupEl = button.closest('[data-pdp-option-group]');
    if (groupEl) groupEl.classList.remove('has-error');
    if (feedbackEl?.classList.contains('is-error')) {
      setFeedback('');
    }
    syncVariantState();
  }

  // --------------------------------------------------------------------------
  // Quantity controls
  // --------------------------------------------------------------------------
  /**
   * Update the readonly quantity input while keeping it at one or above.
   */
  function updateQuantity(delta) {
    if (!quantityInput) return;
    const current = Number(quantityInput.value || 1);
    const next = Math.max(1, current + delta);
    quantityInput.value = String(next);
  }

  // --------------------------------------------------------------------------
  // Size guide drawer
  // --------------------------------------------------------------------------
  /**
   * Open the size guide drawer and lock background scrolling.
   */
  function openSizeGuide(e) {
    if (window.rflxnsOpenSizeGuide) {
      window.rflxnsOpenSizeGuide(e);
      return;
    }
    if (!sizeGuideDrawer) return;
    sizeGuideDrawer.classList.add('is-open');
    sizeGuideDrawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('size-guide-open');
  }

  /**
   * Close the size guide drawer and restore normal page scrolling.
   */
  function closeSizeGuide(e) {
    if (window.rflxnsCloseSizeGuide) {
      window.rflxnsCloseSizeGuide(e);
      return;
    }
    if (!sizeGuideDrawer) return;
    sizeGuideDrawer.classList.remove('is-open');
    sizeGuideDrawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('size-guide-open');
    sizeGuideOpen?.focus();
  }

  // --------------------------------------------------------------------------
  // Tabbed information panel
  // --------------------------------------------------------------------------
  /**
   * Activate one PDP tab and hide the others.
   */
  function activateTab(tabName) {
    tabButtons.forEach((button) => {
      const isActive = button.dataset.tabTarget === tabName;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    tabPanels.forEach((panel) => {
      const isActive = panel.dataset.pdpTabPanel === tabName;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });
  }

  // --------------------------------------------------------------------------
  // Mobile carousel progress
  // --------------------------------------------------------------------------
  /**
   * Mark one mobile media dot as active.
   */
  function setActiveMobileDot(index) {
    mobileDots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Scroll the mobile carousel to a specific image slide.
   */
  function scrollToMobileSlide(index) {
    const target = mobileSlides[index];
    if (!target || !mobileCarousel) return;
    mobileCarousel.scrollTo({
      left: target.offsetLeft - mobileCarousel.offsetLeft,
      behavior: 'smooth',
    });
  }

  /**
   * Observe the mobile carousel viewport and sync active dots.
   */
  function bindMobileCarouselObserver() {
    if (!mobileCarousel || !mobileSlides.length || !mobileDots.length) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = Number(entry.target.dataset.pdpMobileIndex || 0);
            setActiveMobileDot(index);
          });
        },
        {
          root: mobileCarousel,
          threshold: 0.65,
        }
      );

      mobileSlides.forEach((slide) => observer.observe(slide));
    }

    // Scroll listener fallback ensures smooth dot syncing across all mobile browsers
    mobileCarousel.addEventListener(
      'scroll',
      () => {
        const scrollLeft = mobileCarousel.scrollLeft;
        const slideWidth = mobileCarousel.offsetWidth;
        if (slideWidth > 0) {
          const activeIndex = Math.round(scrollLeft / slideWidth);
          if (activeIndex >= 0 && activeIndex < mobileDots.length) {
            setActiveMobileDot(activeIndex);
          }
        }
      },
      { passive: true }
    );

    mobileDots.forEach((dot) => {
      dot.addEventListener('click', () => {
        scrollToMobileSlide(Number(dot.dataset.pdpMobileDotIndex || 0));
      });
    });
  }

  // --------------------------------------------------------------------------
  // Cart submission
  // --------------------------------------------------------------------------
  /**
   * Toggle submit button busy state during cart requests.
   */
  let submittingSafetyTimer = null;

  function setSubmittingState(submitting, submitter) {
    if (!productForm) return;
    productForm.dataset.submitting = submitting ? 'true' : 'false';

    if (submittingSafetyTimer) {
      clearTimeout(submittingSafetyTimer);
      submittingSafetyTimer = null;
    }

    if (submitting) {
      // 4-second safety fallback prevents permanent lock if network stalls
      submittingSafetyTimer = setTimeout(() => {
        setSubmittingState(false, submitter);
      }, 4000);
    }

    const ctas = Array.from(productForm.querySelectorAll('.rflxns-pdp__cta'));

    ctas.forEach((button) => {
      button.disabled = submitting;
      if (!submitting) {
        button.removeAttribute('aria-disabled');
        if (button.dataset.originalLabel) {
          button.textContent = button.dataset.originalLabel;
        } else {
          const action = button.dataset.productAction;
          if (action === 'buy-now') button.textContent = 'Buy now';
          else if (action === 'add') button.textContent = 'Add to bag';
        }
      }
    });

    if (!submitter) return;

    if (submitting) {
      submitter.dataset.originalLabel = submitter.dataset.originalLabel || submitter.textContent;
      submitter.textContent = submitter.dataset.productAction === 'buy-now'
        ? 'Securing...'
        : 'Adding...';
    }
  }

  /**
   * Track last clicked submit button for bulletproof in-app browser submitter detection.
   */
  let lastClickedSubmitter = null;
  if (productForm) {
    productForm.addEventListener('click', (e) => {
      const btn = e.target.closest('button[type="submit"], input[type="submit"], [data-product-action]');
      if (btn) lastClickedSubmitter = btn;
    });
  }

  /**
   * Submit the current PDP form through the shared cart transport service.
   */
  async function submitProductForm(event) {
    if (!productForm) return;

    if (productForm.dataset.submitting === 'true') {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      return;
    }

    const submitter = event.submitter || lastClickedSubmitter || productForm.querySelector('[data-product-action="add"]');
    const action = submitter?.dataset.productAction || 'add';

    if (root.hasAttribute('data-is-product-sold-out') || submitter?.hasAttribute('data-waitlist-trigger') || submitter?.classList.contains('rflxns-pdp__cta--waitlist')) {
      event.preventDefault();
      const waitlistBox = root.querySelector('[data-pdp-waitlist-box]');
      const emailInput = waitlistBox?.querySelector('#rflxns-waitlist-email');
      if (waitlistBox) {
        waitlistBox.classList.add('is-visible');
        waitlistBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
        emailInput?.focus();
      }
      return;
    }

    if (!hasSelectedAllOptions()) {
      event.preventDefault();
      setFeedback('PLEASE SELECT A SIZE TO PROCEED', 'is-error');
      optionGroups.forEach((group) => {
        if (!group.querySelector('[data-pdp-option-button].is-active')) {
          group.classList.add('has-error');
          setTimeout(() => group.classList.remove('has-error'), 800);
        }
      });
      optionGroups[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const variant = syncVariantState();

    if (!variant || !variant.available) {
      event.preventDefault();
      const waitlistBox = root.querySelector('[data-pdp-waitlist-box]');
      const emailInput = waitlistBox?.querySelector('#rflxns-waitlist-email');
      if (waitlistBox) {
        waitlistBox.classList.add('is-visible');
        waitlistBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
        emailInput?.focus();
      }
      setFeedback('This piece is currently archived. Join the restock list below.', 'is-notice');
      return;
    }

    if (!window.rflxnsCartService) {
      // Fallback: If cart service is missing, allow standard native submission to /cart/add
      if (action === 'buy-now') {
        let returnInput = productForm.querySelector('input[name="return_to"]');
        if (!returnInput) {
          returnInput = document.createElement('input');
          returnInput.type = 'hidden';
          returnInput.name = 'return_to';
          returnInput.value = '/checkout';
          productForm.appendChild(returnInput);
        }
      }
      return; // allow native submit to proceed without event.preventDefault()
    }

    event.preventDefault();
    const formData = new FormData(productForm);
    setSubmittingState(true, submitter);
    setFeedback('');

    try {
      const { ok, data } = await window.rflxnsCartService.addFormData(formData, {
        openDrawer: action !== 'buy-now',
        showSignal: action !== 'buy-now',
      });

      if (!ok) {
        throw new Error(data?.description || data?.message || 'Unable to update the cart.');
      }

      if (action === 'buy-now') {
        let discountCode = '';
        try {
          const urlParams = new URLSearchParams(window.location.search);
          discountCode = urlParams.get('discount') || localStorage.getItem('rflxns-discount-code') || '';
        } catch (_) {}

        const rootUrl = window.Shopify?.routes?.root || '/';
        const targetUrl = discountCode && discountCode.trim()
          ? `${rootUrl}checkout?discount=${encodeURIComponent(discountCode.trim().toUpperCase())}`
          : `${rootUrl}checkout`;

        window.location.href = targetUrl;
        return;
      }

      setFeedback('Piece added to bag.', 'is-success');
    } catch (error) {
      setFeedback(error.message || 'Unable to update the cart.', 'is-error');
    } finally {
      setSubmittingState(false, submitter);
    }
  }

  // --------------------------------------------------------------------------
  // Event wiring
  // --------------------------------------------------------------------------
  optionButtons.forEach((button) => {
    button.addEventListener('click', () => selectOption(button));
  });

  quantityButtons.forEach((button) => {
    button.addEventListener('click', () => {
      updateQuantity(Number(button.dataset.pdpQty || 0));
    });
  });

  sizeGuideOpenButtons.forEach((button) => {
    button.addEventListener('click', openSizeGuide);
  });

  sizeGuideCloseButtons.forEach((button) => {
    button.addEventListener('click', closeSizeGuide);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && sizeGuideDrawer?.classList.contains('is-open')) {
      closeSizeGuide();
    }
  });

  tabButtons.forEach((button) => {
    button.addEventListener('click', () => {
      activateTab(button.dataset.tabTarget);
    });
  });

  if (productForm) {
    productForm.addEventListener('submit', submitProductForm);
  }

  // Reset submitting state when page is restored from Back-Forward Cache (e.g. user hits back button from /checkout)
  window.addEventListener('pageshow', () => {
    if (productForm) {
      productForm.dataset.submitting = 'false';
    }
    setSubmittingState(false);
  });



  // --------------------------------------------------------------------------
  // Mobile Sticky Checkout Bar Controller
  // --------------------------------------------------------------------------
  const mainCta = productForm ? productForm.querySelector('[data-product-action="add"]') : null;
  const stickyBar = document.querySelector('[data-pdp-sticky-bar]');
  const stickySubmit = stickyBar ? stickyBar.querySelector('[data-pdp-sticky-submit]') : null;
  const footerEl = document.getElementById('footer-static');

  if (mainCta && stickyBar) {
    let ctaIsVisible = true;
    let footerIsVisible = false;

    const updateStickyVisibility = () => {
      if (window.innerWidth <= 768 && !ctaIsVisible && !footerIsVisible) {
        stickyBar.classList.add('is-visible');
      } else {
        stickyBar.classList.remove('is-visible');
      }
    };

    const ctaObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        ctaIsVisible = entry.isIntersecting;
        updateStickyVisibility();
      });
    }, {
      root: null,
      threshold: 0
    });
    ctaObserver.observe(mainCta);

    if (footerEl) {
      const footerObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          footerIsVisible = entry.isIntersecting;
          updateStickyVisibility();
        });
      }, {
        root: null,
        threshold: 0
      });
      footerObserver.observe(footerEl);
    }

    if (stickySubmit && productForm) {
      stickySubmit.addEventListener('click', () => {
        const addBtn = productForm.querySelector('[data-product-action="add"]');
        if (addBtn && typeof addBtn.click === 'function') {
          addBtn.click();
        } else if (typeof productForm.requestSubmit === 'function') {
          productForm.requestSubmit(addBtn);
        } else {
          submitProductForm({ preventDefault: () => {}, submitter: addBtn });
        }
      });
    }
  }

  // --------------------------------------------------------------------------
  // High-End Lightbox Modal (Modern Dialog Controller)
  // --------------------------------------------------------------------------
  const zoomableImages = root.querySelectorAll('[data-pdp-zoomable]');
  const lightbox = document.getElementById('pdp-lightbox');
  const lightboxImg = lightbox ? lightbox.querySelector('.rflxns-lightbox__img') : null;
  const lightboxClose = lightbox ? lightbox.querySelector('.rflxns-lightbox__close') : null;

  if (lightbox && lightboxImg && zoomableImages.length > 0) {
    zoomableImages.forEach((img) => {
      img.addEventListener('click', () => {
        lightboxImg.src = img.src;
        if (typeof lightbox.showModal === 'function') {
          lightbox.showModal();
        } else {
          lightbox.setAttribute('open', '');
        }
        document.body.style.overflow = 'hidden';
      });
    });

    const closeLightbox = () => {
      if (typeof lightbox.close === 'function') {
        lightbox.close();
      } else {
        lightbox.removeAttribute('open');
      }
      document.body.style.overflow = '';
      setTimeout(() => {
        lightboxImg.src = '';
      }, 400);
    };

    if (lightboxClose) {
      lightboxClose.addEventListener('click', closeLightbox);
    }

    // Coordinate native cancel event (triggered by Esc key) with our close routine
    lightbox.addEventListener('cancel', (e) => {
      e.preventDefault();
      closeLightbox();
    });

    // Fallback click-outside light-dismiss for Safari (until closedBy is supported)
    if (!('closedBy' in HTMLDialogElement.prototype)) {
      lightbox.addEventListener('click', (event) => {
        if (event.target === lightboxClose) return;

        const content = lightbox.querySelector('.rflxns-lightbox__content');
        if (content) {
          const rect = content.getBoundingClientRect();
          const isInsideContent = (
            rect.top <= event.clientY &&
            event.clientY <= rect.top + rect.height &&
            rect.left <= event.clientX &&
            event.clientX <= rect.left + rect.width
          );

          if (!isInsideContent) {
            closeLightbox();
          }
        }
      });
    }
  }

  // --------------------------------------------------------------------------
  // Waitlist / Restock Notification Form Handler
  // --------------------------------------------------------------------------
  const waitlistForm = root.querySelector('#rflxns-waitlist-form');
  const waitlistMsg = root.querySelector('#rflxns-waitlist-message');
  const waitlistSubmit = root.querySelector('#rflxns-waitlist-submit');

  if (waitlistForm) {
    waitlistForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const emailInput = waitlistForm.querySelector('#rflxns-waitlist-email');
      const email = emailInput?.value?.trim();
      if (!email) return;

      if (waitlistSubmit) {
        waitlistSubmit.disabled = true;
        waitlistSubmit.textContent = 'JOINING...';
      }

      try {
        const formData = new FormData(waitlistForm);
        await fetch(waitlistForm.action, {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (waitlistMsg) {
          waitlistMsg.className = 'rflxns-waitlist-message is-success';
          waitlistMsg.textContent = "✓ You're on the list. We'll email you the moment this piece arrives.";
        }
        if (emailInput) {
          emailInput.disabled = true;
        }
        if (waitlistSubmit) {
          waitlistSubmit.textContent = 'JOINED ✓';
        }
        try {
          const productTitle = document.querySelector('.rflxns-pdp__title')?.textContent?.trim() || 'item';
          localStorage.setItem(`rflxns_waitlist_${encodeURIComponent(productTitle)}`, email);
        } catch (_) {}
      } catch (err) {
        if (waitlistMsg) {
          waitlistMsg.className = 'rflxns-waitlist-message is-success';
          waitlistMsg.textContent = "✓ You're on the list. We'll email you the moment this piece arrives.";
        }
        if (waitlistSubmit) {
          waitlistSubmit.textContent = 'JOINED ✓';
        }
      }
    });

    try {
      const productTitle = document.querySelector('.rflxns-pdp__title')?.textContent?.trim() || 'item';
      const existing = localStorage.getItem(`rflxns_waitlist_${encodeURIComponent(productTitle)}`);
      if (existing && waitlistMsg) {
        waitlistMsg.className = 'rflxns-waitlist-message is-success';
        waitlistMsg.textContent = `✓ You're registered on the restock waitlist (${existing}).`;
        if (waitlistSubmit) waitlistSubmit.textContent = 'ON LIST ✓';
      }
    } catch (_) {}
  }

  // --------------------------------------------------------------------------
  // Initial sync & Deep-link variant pre-selection (e.g. Meta DPA / Google Shopping)
  // --------------------------------------------------------------------------
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const paramVariant = urlParams.get('variant');
    if (paramVariant && variants.length > 0) {
      const targetVariant = variants.find((v) => String(v.id) === String(paramVariant));
      if (targetVariant && targetVariant.options && targetVariant.options.length > 0) {
        targetVariant.options.forEach((optVal, optIdx) => {
          const optPos = optIdx + 1;
          const matchingPill = root.querySelector(`[data-pdp-option-button][data-option-position="${optPos}"][data-option-value="${optVal}"]`);
          if (matchingPill && !matchingPill.disabled) {
            selectOption(matchingPill);
          }
        });
      }
    }
  } catch (_) {}

  syncVariantState();
  bindMobileCarouselObserver();
  updateOptionPillAvailability();
});
